import { Layout } from '@/components'
import { Loading } from '@/components'
import GoogleMap from '@/components/GoogleMap'
import SwiperGoogleMap from '@/components/SwiperGoogleMap'
import { PdfSelect } from '@/components/molecules/spots/PdfSelect'
import { SpotList } from '@/components/molecules/spots/SpotList'
import { currentCourse, isChangeCurrentCourse } from '@/features/spotMapSlice'
import { raundUrl } from '@/lib/custom'
import { useCampaignSlug } from '@/lib/useCampaignSlug'
import { useFetchGet } from '@/lib/useFetch'
import { useTracking } from '@/lib/useTracking'
import type { RootState } from '@/store/store'
import styles from '@/styles/GoogleMap.module.scss'
import type { NextPage } from 'next'
import React, { useState, useEffect, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import useSWR from 'swr'
export { getStaticPaths, getStaticProps } from '@/lib/getStaticFunctions'

const Page: NextPage = () => {
  const { t } = useTranslation()
  const { gen, LOCAL_STORAGE_SPOT_SELECTION_KEY } = useCampaignSlug()
  const dispatch = useDispatch()
  const [currentSpots, setCurrentSpots] = useState<Api.SpotInSheetObject[]>()
  const [isAvailable, setAvailable] = useState(true)
  const [spotSelection, setSpotSelection] = useState<string>('map')
  useTracking({ page: 'spots' })

  const { getWithAuth: getSheets } = useFetchGet<Api.SheetsResBody>({})

  const spotCurrentPositionSelector = useSelector((state: RootState) => state.spotMap.currentPosition)
  const spotWatchIdStateSelector = useSelector((state: RootState) => state.spotMap.watchId)
  const currentCourseId = useSelector((state: RootState) => state.spotMap.currentCourse)
  const isChangeCourse = useSelector((state: RootState) => state.spotMap.isChangeCurrentCourse)
  const { getWithoutAuth: campaignGet } = useFetchGet<{ campaign: Api.CampaignResBody }>({})
  const { data: campaignData } = useSWR<{ campaign: Api.CampaignResBody }>(raundUrl.campaign, campaignGet)
  const { data: SheetsData, error: sheetsFetchError } = useSWR<Api.SheetsResBody>(raundUrl.sheets, getSheets, {
    revalidateOnFocus: false,
  })

  const sortSpots = (v1: Api.SpotInSheetObject, v2: Api.SpotInSheetObject): number => {
    if (v1.displayNumber < v2.displayNumber) {
      return -1
    } else if (v1.displayNumber > v2.displayNumber) {
      return 1
    } else {
      if (v1.id < v2.id) {
        return -1
      } else {
        return 1
      }
    }
  }

  useEffect(() => {
    if (localStorage.getItem(LOCAL_STORAGE_SPOT_SELECTION_KEY)) {
      setSpotSelection(localStorage.getItem(LOCAL_STORAGE_SPOT_SELECTION_KEY))
      localStorage.setItem(LOCAL_STORAGE_SPOT_SELECTION_KEY, 'map')
    }
  }, [])

  useEffect(() => {
    if (!campaignData?.campaign) return

    // listType
    // 1: MAP & リスト（デフォルト）
    // 2: MAPのみ
    // 3: リストのみ
    if (campaignData?.campaign.listType === 3) {
      if (
        localStorage.getItem(LOCAL_STORAGE_SPOT_SELECTION_KEY) === null ||
        localStorage.getItem(LOCAL_STORAGE_SPOT_SELECTION_KEY) === 'map'
      ) {
        setSpotSelection('list')
      }
    }
  }, [campaignData])

  useEffect(() => {
    if (!SheetsData) return
    if (!SheetsData.sheets) return
    if (!campaignData) return
    if (isChangeCourse) return
    if (!campaignData?.campaign.defaultSheetId) return
    // 初期値のシートをセット
    dispatch(currentCourse(campaignData?.campaign.defaultSheetId))
  }, [SheetsData, campaignData])

  useEffect(() => {
    if (!SheetsData) return
    if (!SheetsData.sheets) return

    const currentCourseSheet = SheetsData.sheets.find(({ id }) => id == currentCourseId)
    if (currentCourseSheet && currentCourseSheet.spots) {
      setCurrentSpots(currentCourseSheet.spots.sort(sortSpots))
    } else {
      setCurrentSpots(SheetsData.sheets.find(({ id }) => id == 'all').spots.sort(sortSpots))
    }
    setAvailable(false)
  }, [SheetsData, currentCourseId])

  useEffect(() => {
    navigator.geolocation.clearWatch(spotWatchIdStateSelector)
  }, [spotSelection])

  if (isAvailable) return <Loading />
  if (currentSpots === undefined) return <div>{''}</div>
  if (sheetsFetchError) return <div>{''}</div>
  if (!currentSpots) return <div>{''}</div>
  if (!SheetsData) return <div>{''}</div>

  return (
    <Layout title={t('spot.title', 'スポット一覧')}>
      <section>
        <div className={`${spotSelection === 'list' ? 'c-contents relative pt-4 pb-10' : 'c-contents relative'}`}>
          {campaignData?.campaign.listType === 1 && (
            <div
              className={`${styles.c_switch}
           ${spotSelection === 'list' ? `${styles.isList}` : ''}
           ${spotSelection === 'coupon' ? `${styles.isCoupon}` : ''}
           shadow-primary`}
            >
              <button
                type="button"
                onClick={() => {
                  setSpotSelection('map')
                }}
                className={`${styles.c_switch_button} ${spotSelection !== 'map' ? 'text-[#8c8c8c]' : 'text-sub'}`}
              >
                {t('spot.map', 'マップ')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSpotSelection('list')
                }}
                className={`${styles.c_switch_button} ${spotSelection !== 'list' ? 'text-[#8c8c8c]' : 'text-sub'}`}
              >
                {t('spot.list', 'リスト')}
              </button>

              {SheetsData.sheets?.some((sheet) => sheet.id === 'coupon' && sheet.spots) && (
                <button
                  type="button"
                  onClick={() => {
                    setSpotSelection('coupon')
                  }}
                  className={`${styles.c_switch_button} ${spotSelection !== 'coupon' ? 'text-[#8c8c8c]' : 'text-sub'}`}
                >
                  {t('spot.coupon', 'クーポン')}
                </button>
              )}
            </div>
          )}

          {spotSelection === 'list' && (
            <div className="relative mx-4 mt-8 flex items-center justify-between">
              <div>
                <PdfSelect maps={campaignData?.campaign.maps} mapCaption={campaignData?.campaign.mapCaption} />

                <div className="flex items-center justify-between">
                  <div className={`c-select ${styles.c_list_select} bg-[#fff] shadow-primary`}>
                    <select
                      className="border-none text-sm font-bold"
                      value={currentCourseId}
                      onChange={(e) => {
                        dispatch(currentCourse(e.target.value))
                        dispatch(isChangeCurrentCourse(true))
                      }}
                    >
                      {SheetsData.sheets?.map((v) => {
                        if (!v.spots) return
                        return (
                          <Fragment key={v.id}>
                            <option value={v.id}>{v.name}</option>
                          </Fragment>
                        )
                      })}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {spotSelection === 'list' && (
            <div className="border-gray-200 c-shadow_top bg-white mt-3 border-t px-4 pb-6">
              {currentSpots?.map((spot, i) => {
                return (
                  <Fragment key={spot.id}>
                    <SpotList
                      link={gen('/spot/detail/')}
                      query={`${spot.id}`}
                      title={spot.name}
                      address={spot.address}
                      state={spot.stampStatus}
                      img={spot.image ? spot.image : ''}
                      course={spot.sheetNames || [t('spot_detail.other_recommendation', 'その他おすすめ')]}
                      isCoupon={spot.isCoupon}
                      hasStamp={!!spot.stamp}
                      point={
                        campaignData?.campaign.type === 2 || campaignData?.campaign.type === 3
                          ? spot.stamp?.point
                          : undefined
                      }
                      spotCrowded={spot.crowded}
                    />
                  </Fragment>
                )
              })}
            </div>
          )}

          {spotSelection === 'map' && (
            <>
              {spotCurrentPositionSelector ? (
                <div className={styles.c_overlay}>
                  <div className={styles.c_overlay_content}>
                    <div />
                    <div />
                    <div />
                  </div>
                </div>
              ) : (
                ''
              )}
              <div className={`relative ${styles.c_google_map_wrap}`}>
                {SheetsData.sheets?.map((sheet, idx) => {
                  if (sheet.id === 'coupon') {
                    if (sheet.spots) {
                      return (
                        <GoogleMap
                          key={sheet.id}
                          spotInSheet={currentSpots}
                          hasCoupon={true}
                          center={campaignData?.campaign.center}
                          threshold={campaignData?.campaign.custom === 'SHOKYOTO2025' ? 10 : 0.25}
                          defaultZoom={campaignData?.campaign.custom === 'SHOKYOTO2025' ? 5 : 13}
                          maxZoom={12}
                          campaign={campaignData.campaign}
                        />
                      )
                    }
                    return (
                      <GoogleMap
                        key={sheet.id}
                        spotInSheet={currentSpots}
                        hasCoupon={false}
                        center={campaignData?.campaign.center}
                        threshold={campaignData?.campaign.custom === 'SHOKYOTO2025' ? 10 : 0.25}
                        defaultZoom={campaignData?.campaign.custom === 'SHOKYOTO2025' ? 5 : 13}
                        maxZoom={12}
                        campaign={campaignData.campaign}
                      />
                    )
                  }
                })}

                <div className={`${styles.c_google_map_select}`}>
                  <div className={`c-select ${styles.c_google_select}`}>
                    <select
                      className="border-none text-xs font-bold"
                      value={currentCourseId}
                      onChange={(e) => {
                        dispatch(currentCourse(e.target.value))
                        dispatch(isChangeCurrentCourse(true))
                      }}
                    >
                      {SheetsData.sheets.map((v) => {
                        if (!v.spots) return

                        return (
                          <Fragment key={v.id}>
                            <option value={v.id}>{v.name}</option>
                          </Fragment>
                        )
                      })}
                    </select>
                  </div>
                </div>
                <div className={'c_illustration_map'}>
                  <PdfSelect maps={campaignData?.campaign.maps} mapCaption={campaignData?.campaign.mapCaption} />
                </div>
                <SwiperGoogleMap spotInSheet={currentSpots} />
              </div>
            </>
          )}

          {/* クーポン一覧 */}
          {spotSelection === 'coupon' && (
            <>
              <div className="border-gray-200 c-shadow_top bg-white mt-14 border-t px-4 pb-6">
                {SheetsData.sheets[0].spots?.map((spot, i) => {
                  if (spot.isCoupon) {
                    return (
                      <Fragment key={spot.id}>
                        <SpotList
                          link={gen('/spot/detail/')}
                          query={`${spot.id}`}
                          title={spot.name}
                          address={spot.address}
                          state={spot.stamp === null ? '' : Number(spot.stamp.isHas)}
                          img={spot.image ? spot.image : ''}
                          course={spot.sheetNames || [t('spot_detail.other_recommendation', 'その他おすすめ')]}
                          isCoupon={spot.isCoupon}
                          hasStamp={!!spot.stamp}
                          point={
                            campaignData?.campaign.type === 2 || campaignData?.campaign.type === 3
                              ? spot.stamp?.point
                              : undefined
                          }
                          spotCrowded={spot.crowded}
                        />
                      </Fragment>
                    )
                  }
                })}
              </div>
            </>
          )}
        </div>
      </section>
    </Layout>
  )
}

export default Page
